import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, FileCheck, Shield, Search, Clipboard, Users, Factory, Building2, Leaf, Award, Check, CalendarIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Consulting = () => {
  const [formData, setFormData] = useState({
    name: "",
    organization: "",
    email: "",
    phone: "",
    industry: "",
    services: [],
    preferredDates: [],
    description: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const consultingServices = [
    {
      id: "iso9001",
      name: "ISO 9001 Quality Management",
      description: "Implement a world-class quality management system"
    },
    {
      id: "iso22000",
      name: "ISO 22000 / FSSC 22000 Food Safety",
      description: "GFSI recognized food safety management systems"
    },
    {
      id: "haccp",
      name: "HACCP & GMP",
      description: "Hazard analysis and good manufacturing practices"
    },
    {
      id: "gap",
      name: "GAP / GlobalGAP",
      description: "Good agricultural practices certification"
    },
    {
      id: "sqf",
      name: "Safe Quality Food (SQF)",
      description: "SQF certification and implementation"
    },
    {
      id: "iso31000",
      name: "ISO 31000 Risk Management",
      description: "Enterprise risk management frameworks"
    },
    {
      id: "audit",
      name: "Internal & Supplier Audits",
      description: "Comprehensive auditing services"
    },
    {
      id: "regulatory",
      name: "Regulatory & Market Access",
      description: "Navigate export requirements for USA, Canada, EU & UK"
    }
  ];

  const industries = [
    "Food Manufacturing",
    "Agri-Business / Agriculture",
    "Energy",
    "Service Industry",
    "Government / Public Sector",
    "Healthcare",
    "SME / Small Business",
    "Other"
  ];

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.organization) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.services.length === 0) {
      toast.error("Please select at least one service");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/consulting-inquiry`, {
        ...formData,
        services: formData.services.map(id => 
          consultingServices.find(s => s.id === id)?.name
        ).join(", "),
        preferred_dates: formData.preferredDates.length > 0 
          ? formData.preferredDates.map(d => format(d, 'PPP')).join(", ")
          : null
      });
      
      toast.success("Thank you! We'll contact you within 24 hours.");
      setFormData({
        name: "",
        organization: "",
        email: "",
        phone: "",
        industry: "",
        services: [],
        preferredDates: [],
        description: ""
      });
    } catch (error) {
      toast.error("Failed to submit. Please try again or contact us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Consulting Services | Compliance Management Systems"
        description="Expert consulting for ISO 9001, ISO 22000, FSSC 22000, HACCP, GMP, GAP implementation. Internal audits, supplier audits, regulatory compliance. Jamaica & Caribbean."
        url="/services/consulting"
        keywords={["ISO consulting Jamaica", "food safety consultant", "HACCP implementation", "quality management consulting", "FSSC 22000 certification"]}
      />

      {/* Hero Section */}
      <section className="bg-navy-darker text-white py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-medium mb-6">
              <FileCheck className="h-4 w-4" />
              Professional Consulting
            </div>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-6">
              Consulting Services
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Navigating intricate local and international regulatory landscapes is our forte. We pride ourselves on integrity and a seamless approach, offering customized solutions tailored to your industry's unique needs.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading font-bold text-3xl text-primary mb-4">
              Our Consulting Expertise
            </h2>
            <p className="text-muted-foreground">
              We help you achieve or maintain globally recognized standards. Click on any service to learn more.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: Shield, title: "ISO 9001", desc: "Quality Management Systems", link: "/services/iso-9001" },
              { icon: Factory, title: "ISO 22000 / FSSC", desc: "Food Safety Management", link: "/services/iso-22000" },
              { icon: Clipboard, title: "HACCP & GMP", desc: "Food Safety Controls", link: "/services/haccp-gmp" },
              { icon: Leaf, title: "GAP / GlobalGAP", desc: "Agricultural Practices", link: "/services/gap-globalgap" },
              { icon: Award, title: "SQF", desc: "Safe Quality Food", link: "/services/sqf" },
              { icon: Search, title: "Auditing", desc: "Internal & Supplier Audits", link: "/services/auditing" },
              { icon: Building2, title: "Risk Management", desc: "ISO 31000 Framework", link: "/services/iso-31000" },
              { icon: FileCheck, title: "Market Access", desc: "Export Compliance", link: "/services/market-access" }
            ].map((item, index) => (
              <Link key={index} to={item.link} className="block group">
                <Card className="text-center hover:shadow-lg transition-all duration-300 border-2 hover:border-brand/30 h-full group-hover:-translate-y-1">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-brand/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-brand transition-colors">
                      <item.icon className="h-6 w-6 text-brand group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-semibold mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{item.desc}</p>
                    <span className="inline-flex items-center text-xs text-brand font-medium group-hover:text-brand-dark">
                      Learn More <ArrowRight className="ml-1 h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* Why Choose Us */}
          <div className="bg-slate-50 rounded-2xl p-8 lg:p-12">
            <h3 className="font-heading font-bold text-2xl text-primary mb-8 text-center">
              Why Choose CMS for Consulting?
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                "Embracing a passionate commitment to quality, food safety, and organizational success",
                "Deep insight into manufacturing and service industry nuances",
                "Your strategic ally in compliance, risk mitigation, and sustainable growth",
                "Comprehensive management solutions tailored to your needs",
                "Meticulous auditing services, leaving no detail overlooked",
                "Effortlessly navigate export requirements for USA, Canada, Europe & UK"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-brand flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Request Form */}
      <section className="py-20 bg-slate-100" id="request-form">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <Card className="shadow-xl">
              <CardHeader className="bg-brand text-white rounded-t-lg">
                <CardTitle className="font-heading text-2xl text-center">
                  Request Consulting Support
                </CardTitle>
                <p className="text-center text-white/80 mt-2">
                  Tell us about your needs and we'll respond within 24 hours
                </p>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="organization">Organization *</Label>
                      <Input
                        id="organization"
                        value={formData.organization}
                        onChange={(e) => setFormData({...formData, organization: e.target.value})}
                        placeholder="Company name"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="+1 (876) 000-0000"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Select
                      value={formData.industry}
                      onValueChange={(value) => setFormData({...formData, industry: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select your industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {industries.map((ind) => (
                          <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label>Services of Interest *</Label>
                    <div className="grid md:grid-cols-2 gap-3">
                      {consultingServices.map((service) => (
                        <div
                          key={service.id}
                          className={`flex items-start space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                            formData.services.includes(service.id)
                              ? 'border-brand bg-brand/5'
                              : 'border-slate-200 hover:border-brand/50'
                          }`}
                          onClick={() => handleServiceToggle(service.id)}
                        >
                          <div className={`h-4 w-4 shrink-0 rounded-sm border shadow flex items-center justify-center ${
                            formData.services.includes(service.id)
                              ? 'bg-primary border-primary text-primary-foreground'
                              : 'border-primary'
                          }`}>
                            {formData.services.includes(service.id) && (
                              <Check className="h-3 w-3" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{service.name}</p>
                            <p className="text-xs text-muted-foreground">{service.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Preferred Consultation Dates (Optional)</Label>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.preferredDates.length > 0 
                            ? formData.preferredDates.map(d => format(d, 'MMM d')).join(", ")
                            : "Select preferred dates"
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="multiple"
                          selected={formData.preferredDates}
                          onSelect={(dates) => setFormData({...formData, preferredDates: dates || []})}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                        <div className="p-2 border-t flex gap-2">
                          {formData.preferredDates.length > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="flex-1"
                              onClick={() => setFormData({...formData, preferredDates: []})}
                            >
                              Clear
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            className="flex-1 bg-brand hover:bg-brand-dark"
                            onClick={() => setCalendarOpen(false)}
                          >
                            Done
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-muted-foreground">Click to select multiple dates that work for you</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Brief Description of Your Needs</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Tell us about your current challenges, goals, or specific requirements..."
                      rows={4}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-brand hover:bg-brand-dark text-white py-6 text-lg"
                    disabled={submitting}
                  >
                    {submitting ? "Submitting..." : "Request Consulting Support"}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Consulting;
